/**
 * @fileoverview Verify/resend mutations for whichever emailed-code challenge is pending.
 * @description TwoFactorForm serves two emailed-code steps that share every bit of UI but hit
 * different endpoints: a LOGIN challenge (POST /identity/mfa/verify and /mfa/resend) and an
 * ACTIVATION challenge (POST /identity/activation/mfa/verify and /resend). Hooks can't be called
 * conditionally, so this calls all four and hands back the pair matching the challenge's purpose,
 * narrowed to the members the form uses so the two pairs share one type.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useResendActivationCode } from '@/features/auth/hooks/useResendActivationCode'
import { useResendTwoFactorCode } from '@/features/auth/hooks/useResendTwoFactorCode'
import { useVerifyActivationCode } from '@/features/auth/hooks/useVerifyActivationCode'
import { useVerifyTwoFactor } from '@/features/auth/hooks/useVerifyTwoFactor'

import type { TwoFactorChallengePurpose } from '@/shared/lib/auth/auth.types'
import type { ApiError } from '@/shared/types/http.types'

interface ChallengeMutationOptions {
  onSuccess?: () => void
  onError?: (error: ApiError) => void
}

export interface ChallengeCodeMutation<TVariables> {
  mutate: (variables: TVariables, options?: ChallengeMutationOptions) => void
  reset: () => void
  isPending: boolean
  isError: boolean
  error: ApiError | null
}

interface ChallengeCodeMutations {
  verify: ChallengeCodeMutation<{ challengeId: string; otp: string }>
  resend: ChallengeCodeMutation<{ challengeId: string }>
}

/**
 * @description Narrows a TanStack mutation to the members TwoFactorForm uses.
 * @param {object} mutation - The TanStack mutation result.
 * @returns {ChallengeCodeMutation<TVariables>} The narrowed mutation.
 */
function narrow<TVariables>(mutation: {
  mutate: (variables: TVariables, options?: ChallengeMutationOptions) => void
  reset: () => void
  isPending: boolean
  isError: boolean
  error: ApiError | null
}): ChallengeCodeMutation<TVariables> {
  return {
    mutate: (variables, options) => mutation.mutate(variables, options),
    reset: mutation.reset,
    isPending: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
  }
}

/**
 * @description Returns the verify/resend mutations for the given challenge purpose.
 * @param {TwoFactorChallengePurpose | undefined} purpose - The pending challenge's purpose
 * (absent means LOGIN).
 * @returns {ChallengeCodeMutations} The matching verify and resend mutations.
 */
export function useChallengeCodeMutations(
  purpose: TwoFactorChallengePurpose | undefined,
): ChallengeCodeMutations {
  const loginVerify = useVerifyTwoFactor()
  const loginResend = useResendTwoFactorCode()
  const activationVerify = useVerifyActivationCode()
  const activationResend = useResendActivationCode()

  return purpose === 'ACTIVATION'
    ? { verify: narrow(activationVerify), resend: narrow(activationResend) }
    : { verify: narrow(loginVerify), resend: narrow(loginResend) }
}
